import { type Ref } from 'vue';
import type { TableElement } from '../model/form.types';
import { TABLE_CONSTRAINTS } from '../lib/table-constants';
import { getResolvedColumnWidthsPx } from '../lib/table-column-utils';
import { trackReactiveDep } from '../lib/track-reactive-dep';
import type { ResizeState } from './useTableResize';

const ROW_BUTTON_HEIGHT = 25;
const COLUMN_BUTTON_SIZE = 24;

export function useTableButtonPositioning(
  tableElement: Ref<TableElement>,
  actualRowHeights: Ref<number[]>,
  gridRef: Ref<HTMLElement | null>,
  resizing?: Ref<ResizeState | null>,
  layoutVersion?: Ref<number>
) {
  const getVisibleRowCell = (rowIndex: number): HTMLElement | null => {
    if (!gridRef.value) return null;

    const rowCells = gridRef.value.querySelectorAll(
      `.table-cell:not(.hidden)[data-row-index="${rowIndex}"]`
    ) as NodeListOf<HTMLElement>;

    const mergedRootCell = Array.from(rowCells).find((cell) => {
      const rowSpan = Number.parseInt(cell.getAttribute('aria-rowspan') || '1', 10);
      return rowSpan > 1;
    });

    if (mergedRootCell && mergedRootCell.offsetHeight > 0) {
      return mergedRootCell;
    }

    for (const cell of Array.from(rowCells)) {
      if (cell.offsetHeight > 0) {
        return cell;
      }
    }

    return null;
  };

  const getRowHeights = () => {
    const totalRows = tableElement.value.properties.rows;
    const liveRowHeights = resizing?.value?.type === 'row' ? resizing.value.currentHeights : null;
    if (liveRowHeights?.length === totalRows) {
      return liveRowHeights;
    }

    const measuredRowHeights = actualRowHeights.value;
    if (measuredRowHeights.length === totalRows) {
      return measuredRowHeights;
    }

    const storedRowHeights = tableElement.value.properties.rowHeights;
    if (storedRowHeights?.length === totalRows) {
      return storedRowHeights;
    }

    return new Array<number>(totalRows).fill(TABLE_CONSTRAINTS.DEFAULT_ROW_HEIGHT);
  };

  const getRowSelectButtonStyle = (rowIndex: number) => {
    if (!gridRef.value) return {};

    const rowCell = getVisibleRowCell(rowIndex);
    if (rowCell) {
      const centerTop = rowCell.offsetTop + (rowCell.offsetHeight - ROW_BUTTON_HEIGHT) / 2;

      return {
        top: `${centerTop}px`,
      };
    }

    const rowHeights = getRowHeights();
    let top = 0;

    for (let i = 0; i < rowIndex; i++) {
      top += rowHeights[i] || TABLE_CONSTRAINTS.DEFAULT_ROW_HEIGHT;
    }

    const rowHeight = rowHeights[rowIndex] || TABLE_CONSTRAINTS.DEFAULT_ROW_HEIGHT;
    const centerTop = top + (rowHeight - ROW_BUTTON_HEIGHT) / 2;

    return {
      top: `${centerTop}px`,
    };
  };

  const getVisibleColCell = (colIndex: number): HTMLElement | null => {
    if (!gridRef.value) return null;

    const colCells = gridRef.value.querySelectorAll(
      `.table-cell:not(.hidden)[data-col-index="${colIndex}"]`
    ) as NodeListOf<HTMLElement>;

    for (const cell of Array.from(colCells)) {
      // A column-spanning cell's offsetWidth covers multiple columns, so centering on
      // it would place the selector over the merged region instead of this column.
      const colSpan = Number.parseInt(cell.getAttribute('aria-colspan') || '1', 10);
      if (colSpan > 1) continue;

      if (cell.offsetWidth > 0) {
        return cell;
      }
    }

    return null;
  };

  const getColSelectButtonStyle = (colIndex: number) => {
    if (!gridRef.value) return {};

    // Registers a reactive dep so the selector re-measures whenever the layout shifts
    // (e.g. a column grows to fit newly added cell content). The value is otherwise unused.
    trackReactiveDep(layoutVersion);

    // The real rendered layout is the source of truth: columns use `fr` tracks that grow to
    // fit their content beyond the stored proportional widths, so measure the actual cell.
    const colCell = getVisibleColCell(colIndex);
    if (colCell) {
      const centerLeft = colCell.offsetLeft + (colCell.offsetWidth - COLUMN_BUTTON_SIZE) / 2;

      return {
        left: `${centerLeft}px`,
      };
    }

    const columnWidths = tableElement.value.properties.columnWidths;
    const columns = tableElement.value.properties.columns;
    const resolvedWidths = getResolvedColumnWidthsPx(columnWidths, columns, gridRef.value.offsetWidth);

    let left = 0;
    for (let i = 0; i < colIndex; i++) {
      left += resolvedWidths[i];
    }

    const colWidth = resolvedWidths[colIndex];
    const centerLeft = left + (colWidth - COLUMN_BUTTON_SIZE) / 2;

    return {
      left: `${centerLeft}px`,
    };
  };

  return {
    getRowSelectButtonStyle,
    getColSelectButtonStyle,
  };
}
